import { FC, ReactNode } from 'react'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { css, styled } from 'styled-components'

// --- Component Parts ---
// Re-exporting Radix parts, potentially wrapped or styled.

export const DropdownMenuRoot = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal
export const DropdownMenuContent = DropdownMenuPrimitive.Content
export const DropdownMenuItem = DropdownMenuPrimitive.Item

export const DropdownMenuLabel = DropdownMenuPrimitive.Label
export const DropdownMenuGroup = DropdownMenuPrimitive.Group
export const DropdownMenuSeparator = DropdownMenuPrimitive.Separator
export const DropdownMenuCheckboxItem = DropdownMenuPrimitive.CheckboxItem
export const DropdownMenuItemIndicator = DropdownMenuPrimitive.ItemIndicator
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup
export const DropdownMenuRadioItem = DropdownMenuPrimitive.RadioItem
export const DropdownMenuSub = DropdownMenuPrimitive.Sub
export const DropdownMenuSubTrigger = DropdownMenuPrimitive.SubTrigger
export const DropdownMenuSubContent = DropdownMenuPrimitive.SubContent
export const DropdownMenuArrow = DropdownMenuPrimitive.Arrow

// --- Example Usage (Optional - can be removed or moved to a storybook file) ---
// This is a basic example to show how the parts can be assembled.
// It's not meant to be the final component you'll use directly everywhere.

type DropdownMenuProps = {
  trigger: ReactNode
  children: ReactNode
  // Add any other props your custom DropdownMenu might need, e.g., open, onOpenChange
}

export const DropdownMenu: FC<DropdownMenuProps> = ({ trigger, children }) => (
  <DropdownMenuRoot>
    <StyledDropdownMenuTrigger asChild>{trigger}</StyledDropdownMenuTrigger>
    <DropdownMenuPortal>
      <StyledDropdownMenuContent sideOffset={5}>
        {children}
        {/* <DropdownMenuArrow /> */}
      </StyledDropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
)

// You might want to create a more specific, opinionated Dropdown component
// that uses these primitives, or just export the primitives for composition.

// --- Styled Components ---
// All styled components are now at the end of the file.

const StyledDropdownMenuContent = styled(DropdownMenuPrimitive.Content)`
  /* Radix popper reads this z-index and applies it to data-radix-popper-content-wrapper */
  z-index: 50;
  border-radius: 6px;
  box-shadow: 0 10px 38px -10px rgb(22 23 24 / 35%),
    0 10px 20px -15px rgb(22 23 24 / 20%);
  background-color: ${(p) => p.theme.mainMenu.background};
  padding: 2px 8px;
  min-width: 220px;
`

const DropdownMenuCSS = css`
  display: flex;
  position: relative;
  align-items: center;
  margin: 8px 0;
  outline: none;
  border-radius: 3px;
  padding: 8px;
  line-height: 1;
  color: ${(p) => p.theme.text};
  font-size: 16px;
  user-select: none;

  &[data-disabled] {
    color: ${(p) => p.theme.disabledText};
    pointer-events: none;
  }

  &[data-highlighted] {
    background-color: ${(p) => p.theme.highlight};
    color: ${(p) => p.theme.invertedText};
  }
`

export const StyledDropdownMenuItem = styled(DropdownMenuPrimitive.Item)`
  ${DropdownMenuCSS}
`

export const StyledNonClickableDropdownMenuItem = styled.div`
  ${DropdownMenuCSS}
  pointer-events: none;
`

const StyledDropdownMenuTrigger = styled(DropdownMenuPrimitive.Trigger)``
// Add other styled parts as needed: Label, Separator, CheckboxItem, RadioItem, SubTrigger, etc.

export const StyledDropdownMenuSeparator = styled(
  DropdownMenuPrimitive.Separator,
)`
  background-color: ${(props) => props.theme.border};
  height: 1px;
`

// Re-assign exported components to use the styled versions
// It's generally better to export the styled versions directly if they are the default.
// However, to stick to "styled components at the end", we define them at the end
// and then can choose how to export them. For now, the top-level exports
// are the raw primitives, and the example uses the styled ones.
// If the intent is for the main exports (DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem)
// to *be* the styled versions, those export lines at the top should be changed.

// For now, let's assume the example `DropdownMenu` is the primary way to use these styled components.
// The individual primitive exports (DropdownMenuTrigger, etc.) remain unstyled primitives.
// If you want `export const DropdownMenuTrigger = StyledDropdownMenuTrigger;` etc. at the top,
// that's also possible but means the styled components aren't *strictly* only at the end
// in terms of where the `styled()` call happens vs where the export happens.

// The current setup is:
// - Raw primitives exported at the top.
// - Styled versions defined at the bottom.
// - The example `DropdownMenu` composite component uses the styled versions.
